export function shippingProgress(cart, threshold) {
  const physicalTotal = cart.items.reduce((sum, item) => sum + (item.requires_shipping ? item.final_line_price : 0), 0);
  const discount = (cart.cart_level_discount_applications || []).reduce((sum, application) => sum + application.total_allocated_amount, 0);
  const total = Math.max(0, physicalTotal - discount);
  return { total, remaining: Math.max(0, threshold - total), value: Math.min(threshold, total) };
}

export function buildPackUpgrade(cart, product, offer) {
  const line = cart.items.find(item => item.key === offer.lineKey);
  const target = product.variants.find(variant => String(variant.id) === offer.newVariant);
  const current = product.variants.find(variant => String(variant.id) === offer.oldVariant);
  if (!line || line.quantity !== offer.quantity || String(line.variant_id) !== offer.oldVariant || String(line.product_id) !== offer.productId) throw new Error('cart_changed');
  if (line.selling_plan_allocation || Object.keys(line.properties || {}).length) throw new Error('cart_changed');
  if (cart.items.some(item => String(item.variant_id) === offer.newVariant)) throw new Error('cart_changed');
  if (!current || !target || !target.available || String(product.id) !== offer.productId || offer.oldVariant === offer.newVariant) throw new Error('cart_changed');
  if (current.price !== offer.oldPrice || target.price !== offer.newPrice || line.original_price !== offer.oldPrice) throw new Error('cart_changed');
  if (!Number.isSafeInteger(offer.quantity) || offer.quantity < 1 || !Number.isInteger(offer.packOptionIndex) || offer.packOptionIndex < 0 || offer.packOptionIndex >= current.options.length) throw new Error('cart_changed');
  if (current.options.some((option, index) => index !== offer.packOptionIndex && option !== target.options[index])) throw new Error('cart_changed');
  return { updates: { [line.key]: 0, [offer.newVariant]: line.quantity } };
}

export function upgradeWasApplied(cart, offer) {
  return !cart.items.some(item => item.key === offer.lineKey) && cart.items.some(item => String(item.variant_id) === offer.newVariant && item.quantity === offer.quantity);
}

if (typeof document !== 'undefined') {
  let cartBusy = false;
  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const readJson = async (url, options) => {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'request_failed');
    return data;
  };
  const cartSnapshot = () => readJson(`${cartRoot()}cart.js`, { cache: 'no-store' });
  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    return [...new Set(sections)].slice(0, 5);
  };
  const publishCart = (cart, onSuccessDo = '') => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'pack:upgrade', onSuccessDo, cart } }));
    if (document.querySelector('.gf-cart-page-offers')) window.location.reload();
  };
  const completeSections = async (cart, sectionNames) => {
    cart.sections ||= {};
    const missing = sectionNames.filter(section => typeof cart.sections[section] !== 'string');
    if (missing.length) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', missing.join(','));
      Object.assign(cart.sections, await readJson(url.toString(), { cache: 'no-store' }));
    }
    if (sectionNames.some(section => typeof cart.sections[section] !== 'string')) throw new Error('section_refresh_failed');
    return cart;
  };

  class GFDeliveryProgress extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      document.documentElement.addEventListener('cart:change', event => {
        const progress = this.querySelector('progress');
        if (progress && event.detail?.cart?.items) progress.value = shippingProgress(event.detail.cart, Number(progress.max)).value;
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('gf-delivery-progress')) customElements.define('gf-delivery-progress', GFDeliveryProgress);

  document.addEventListener('submit', event => {
    if (cartBusy && event.target.closest('product-form, .cart-drawer__footer, .cart-page')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', async event => {
    const choice = event.target.closest('[data-gf-pack-choice]');
    if (choice) {
      const form = document.forms[choice.dataset.formId];
      const picker = [...document.querySelectorAll('variant-picker')].find(element => element.getAttribute('form-id') === choice.dataset.formId);
      const inputs = form ? Array.from(form.elements).filter(element => element.matches('input[data-option-position]')) : [];
      const targetInput = inputs.find(input => input.dataset.optionPosition === choice.dataset.optionPosition && input.value === choice.dataset.optionValue);
      if (!picker || !targetInput) return;
      event.preventDefault();
      const container = choice.closest('[data-gf-pack-form]');
      if (container.getAttribute('aria-busy') === 'true') return;
      container.setAttribute('aria-busy', 'true');
      const error = container.querySelector('[data-gf-pack-error]');
      error.hidden = true;
      const submitButtons = Array.from(form.elements).filter(input => input.type === 'submit');
      const disabledStates = submitButtons.map(button => [button, button.disabled]);
      disabledStates.forEach(([button]) => { button.disabled = true; });
      const optionValues = inputs.filter(input => input.checked && input.dataset.optionPosition !== choice.dataset.optionPosition).concat(targetInput).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map(input => input.value);
      try {
        await picker.selectCombination({ optionValues, productChange: false });
      } catch {
        error.hidden = false;
      } finally {
        container.removeAttribute('aria-busy');
        disabledStates.forEach(([button, disabled]) => { if (button.isConnected) button.disabled = disabled; });
      }
      return;
    }

    const button = event.target.closest('[data-gf-upgrade]');
    if (!button) return;
    event.preventDefault();
    if (cartBusy) return;
    cartBusy = true;
    const container = button.closest('[data-gf-cart-offers]');
    const error = container.querySelector('[data-gf-offer-error]');
    error.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const offer = {
      lineKey: button.dataset.lineKey,
      oldVariant: button.dataset.oldVariant,
      newVariant: button.dataset.newVariant,
      productId: button.dataset.productId,
      quantity: Number(button.dataset.quantity),
      oldPrice: Number(button.dataset.oldPrice),
      newPrice: Number(button.dataset.newPrice),
      packOptionIndex: Number(button.dataset.packOptionIndex)
    };
    const sections = bundledSections();
    let mutationStarted = false;
    try {
      const [before, product] = await Promise.all([
        cartSnapshot(),
        readJson(`${cartRoot()}products/${encodeURIComponent(button.dataset.productHandle)}.js`, { cache: 'no-store' })
      ]);
      const update = buildPackUpgrade(before, product, offer);
      mutationStarted = true;
      const cart = await readJson(`${cartRoot()}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...update, sections, sections_url: window.location.pathname })
      });
      if (!upgradeWasApplied(cart, offer)) throw new Error('upgrade_not_applied');
      publishCart(await completeSections(cart, sections));
    } catch (failure) {
      if (mutationStarted) {
        try {
          const current = await cartSnapshot();
          const oldPresent = current.items.some(item => String(item.variant_id) === offer.oldVariant);
          const newPresent = current.items.some(item => String(item.variant_id) === offer.newVariant);
          if (!oldPresent && !newPresent) {
            await readJson(`${cartRoot()}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: offer.oldVariant, quantity: offer.quantity }] }) });
          }
          const recovered = await cartSnapshot();
          if (upgradeWasApplied(recovered, offer)) {
            publishCart(await completeSections(recovered, sections));
            return;
          }
        } catch {
          error.textContent = container.dataset.errorMessage;
        }
      }
      error.textContent = failure.message === 'cart_changed' ? container.dataset.staleMessage : container.dataset.errorMessage;
      error.hidden = false;
    } finally {
      cartBusy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
}
